import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Card, Typography, Avatar, IconButton, useTheme } from '@mui/material';
import { ChevronLeft, ChevronRight, FormatQuote, Star } from '@mui/icons-material';

const FOREST = '#163300';
const GREEN  = '#9FE870';

const TestimonialCarousel = ({
  testimonials = [],
  interval = 5000,
  sx = {}
}) => {
  const theme = useTheme();
  const [active, setActive]   = useState(0);
  const [fading, setFading]   = useState(false);
  const [paused, setPaused]   = useState(false);
  const timerRef              = useRef(null);
  const count                 = testimonials.length;

  const goTo = useCallback((idx) => {
    if (fading) return;
    setFading(true);
    setTimeout(() => {
      setActive((idx + count) % count);
      setFading(false);
    }, 280);
  }, [fading, count]);

  const next = useCallback(() => goTo(active + 1), [active, goTo]);
  const prev = useCallback(() => goTo(active - 1), [active, goTo]);

  // Auto-rotate
  useEffect(() => {
    if (paused || count < 2) return;
    timerRef.current = setInterval(next, interval);
    return () => clearInterval(timerRef.current);
  }, [paused, next, interval, count]);

  if (!count) return null;

  const t = testimonials[active];
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{ position: 'relative', ...sx }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Card */}
      <Card
        sx={{
          p: { xs: 3, md: 4 },
          borderRadius: 3,
          border: '1px solid',
          borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(22,51,0,0.1)',
          bgcolor: isDark ? 'background.paper' : 'white',
          boxShadow: isDark ? 'none' : '0 8px 32px rgba(22,51,0,0.07)',
          transition: 'opacity 0.28s ease',
          opacity: fading ? 0 : 1,
          minHeight: 260,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        {/* Quote icon */}
        <FormatQuote
          sx={{
            fontSize: 48,
            color: GREEN,
            opacity: 0.5,
            mb: 1,
            transform: 'scaleX(-1)',
          }}
        />

        {/* Stars */}
        <Box sx={{ display: 'flex', gap: 0.5, mb: 2 }}>
          {[...Array(t.rating ?? 5)].map((_, i) => (
            <Star key={i} sx={{ fontSize: 18, color: '#f59e0b' }} />
          ))}
        </Box>

        {/* Review text */}
        <Typography
          sx={{
            fontSize: { xs: '0.95rem', md: '1.05rem' },
            lineHeight: 1.75,
            color: 'text.secondary',
            fontStyle: 'italic',
            flex: 1,
            mb: 3,
          }}
        >
          "{t.text}"
        </Typography>

        {/* Author */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {t.photo ? (
            <Avatar
              src={t.photo}
              alt={t.name}
              sx={{ width: 52, height: 52, border: `2px solid ${GREEN}` }}
            />
          ) : (
            <Avatar
              sx={{
                width: 52,
                height: 52,
                bgcolor: FOREST,
                color: GREEN,
                fontWeight: 800,
                fontSize: '1.1rem',
                border: `2px solid ${GREEN}`,
              }}
            >
              {t.avatar || t.name?.[0]?.toUpperCase()}
            </Avatar>
          )}
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', lineHeight: 1.3 }}>
              {t.name}
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', fontWeight: 500 }}>
              {t.role}
            </Typography>
            {t.company && (
              <Typography sx={{ fontSize: '0.75rem', color: GREEN, fontWeight: 700 }}>
                {t.company}
              </Typography>
            )}
          </Box>
        </Box>
      </Card>

      {/* Prev / Next arrows */}
      {count > 1 && (
        <>
          <IconButton
            onClick={prev}
            size="small"
            sx={{
              position: 'absolute',
              left: -20,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: FOREST,
              color: GREEN,
              width: 36,
              height: 36,
              boxShadow: '0 4px 12px rgba(22,51,0,0.25)',
              '&:hover': { bgcolor: '#0b1a00' },
              zIndex: 2,
            }}
          >
            <ChevronLeft fontSize="small" />
          </IconButton>

          <IconButton
            onClick={next}
            size="small"
            sx={{
              position: 'absolute',
              right: -20,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: FOREST,
              color: GREEN,
              width: 36,
              height: 36,
              boxShadow: '0 4px 12px rgba(22,51,0,0.25)',
              '&:hover': { bgcolor: '#0b1a00' },
              zIndex: 2,
            }}
          >
            <ChevronRight fontSize="small" />
          </IconButton>
        </>
      )}

      {/* Dot pagination */}
      {count > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2.5 }}>
          {testimonials.map((_, i) => (
            <Box
              key={i}
              onClick={() => goTo(i)}
              sx={{
                width: i === active ? 24 : 8,
                height: 8,
                borderRadius: 4,
                bgcolor: i === active ? FOREST : (isDark ? 'rgba(255,255,255,0.2)' : 'rgba(22,51,0,0.15)'),
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': { bgcolor: i === active ? FOREST : 'rgba(22,51,0,0.35)' },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default TestimonialCarousel;
